import { useState } from "react";
import { invokeAdmin, errMsg } from "../lib/adminApi";
import { useAdminQuery } from "../hooks/useAdminQuery";
import { Banner, CopyId, PillTabs } from "../components/ui";

type PromotionStatus = "pending" | "active" | "rejected";

type Promotion = {
  id: string;
  user_id: string;
  target_kind: string;
  target_id: string;
  kind: string;
  status: string;
  created_at: string;
};

const TABS: { key: PromotionStatus; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "active", label: "Active" },
  { key: "rejected", label: "Rejected" },
];

export default function PromotionsPage() {
  const [tab, setTab] = useState<PromotionStatus>("pending");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Cancellation-safe fetch keyed on the tab (ADM-5).
  const { data, loading, error, refetch } = useAdminQuery(
    () =>
      invokeAdmin<{ promotions: Promotion[] }>("admin_promotions_list", {
        status: tab,
        limit: 50,
      }),
    [tab],
  );
  const promotions = data?.promotions ?? [];

  async function review(p: Promotion, status: "active" | "rejected") {
    // Activate flips a paid boost live; Reject is terminal in the UI (pending-only
    // controls). Confirm first (ADM-4).
    const verb = status === "active" ? "Activate" : "Reject";
    if (
      !window.confirm(
        `${verb} the ${p.kind} promotion on ${p.target_kind} ${p.target_id}?`,
      )
    )
      return;
    setActionLoading(p.id);
    setActionError(null);
    try {
      await invokeAdmin("admin_promotion_update", { id: p.id, status });
      refetch();
    } catch (e) {
      setActionError(errMsg(e));
    } finally {
      setActionLoading(null);
    }
  }

  return (
    <div>
      <h1 className="text-xl font-semibold">Promotions</h1>

      <PillTabs className="mt-4" tabs={TABS} active={tab} onChange={setTab} />

      {error && <Banner className="mt-3">{error}</Banner>}
      {actionError && <Banner className="mt-3">{actionError}</Banner>}

      <div className="mt-4 rounded-lg border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Kind</th>
              <th className="px-3 py-2 font-medium">Target</th>
              <th className="px-3 py-2 font-medium">User</th>
              <th className="px-3 py-2 font-medium">Created</th>
              {tab === "pending" && <th className="px-3 py-2 font-medium"></th>}
            </tr>
          </thead>
          <tbody>
            {promotions.map((p) => (
              <tr key={p.id} className="border-t border-zinc-800 align-top">
                <td className="px-3 py-2 text-zinc-300">{p.kind}</td>
                <td className="px-3 py-2 text-zinc-400 text-xs">
                  <span className="text-zinc-300">{p.target_kind}</span>
                  <span className="ml-2 inline-flex align-middle">
                    <CopyId id={p.target_id} />
                  </span>
                </td>
                <td className="px-3 py-2">
                  <CopyId id={p.user_id} />
                </td>
                <td className="px-3 py-2 text-zinc-400 text-xs">
                  {p.created_at
                    ? new Date(p.created_at).toLocaleDateString()
                    : "—"}
                </td>
                {tab === "pending" && (
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => review(p, "active")}
                        disabled={actionLoading === p.id}
                        className="rounded bg-green-800 px-2 py-0.5 text-xs text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        {actionLoading === p.id ? "…" : "Activate"}
                      </button>
                      <button
                        onClick={() => review(p, "rejected")}
                        disabled={actionLoading === p.id}
                        className="rounded bg-red-800 px-2 py-0.5 text-xs text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {actionLoading === p.id ? "…" : "Reject"}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {promotions.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={tab === "pending" ? 5 : 4}
                  className="px-3 py-4 text-center text-sm text-zinc-500"
                >
                  No {tab} promotions.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td
                  colSpan={tab === "pending" ? 5 : 4}
                  className="px-3 py-4 text-center text-sm text-zinc-500"
                >
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
